/**
 * API client for the deploy-v3 backend.
 *
 * One factory + one ApiRef so the entire frontend gets type-safe access
 * to the 15 backend endpoints. The implementation is a thin wrapper over
 * Backstage's `fetchApi` + `discoveryApi`, which together handle base
 * URL resolution, auth cookie attachment, and error normalization.
 *
 * Why an ApiRef instead of free functions:
 *   - Backstage convention — components consume via `useApi(deployV3ApiRef)`
 *   - Lets us swap the implementation for tests (provide a stub via
 *     `TestApiProvider`) without changing component code
 *   - Plays nicely with React Suspense + the standard error boundaries
 *
 * Error contract:
 *   - 2xx responses → resolved value
 *   - 4xx responses with JSON body → reject with a typed `ApiError` carrying
 *     the structured error code (matches the eng spec §2.4 codes the
 *     frontend already needs to render different UI for)
 *   - 5xx + network → reject with `ApiError{ code: 'network' }`
 */

import { createApiRef } from '@backstage/core-plugin-api';
import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  ApprovalRequest,
  DeployRun,
  Env,
  HeaderState,
  MigrationSummary,
  RunStatus,
} from '@internal/backstage-plugin-deploy-v3-common';

// ── Public types matching the backend response shapes ──────────────
export interface ListRunsResponse {
  rows: DeployRun[];
  nextCursor: string | null;
  /** Per-env totals returned when caller passes multi-env CSV in `env`. */
  envCounts?: Partial<Record<Env, number>>;
}

/** Response shape for the new `/prior-sha` endpoint (Scope D.2). */
/** D3 — one SHA that was rolled back out of a lane (rollback-prd.md §5.4). */
export interface KnownBadEntry {
  fledRunId: string;
  rolledBackByRunId: string;
  rolledBackAt: string | null;
}
/** Keyed by full 40-hex SHA; an empty object is the normal case, not an error. */
export interface KnownBadResponse {
  shas: Record<string, KnownBadEntry>;
}

/** D2 — one candidate SHA to roll a lane back to (rollback-prd.md §5.3). */
export interface RollbackTarget {
  runId?: string;
  sha: string;
  shortSha: string;
  succeededAt: string | null;
  initiator: string;
  /** This SHA was itself rolled back out of the lane before (D3). Flagged,
   *  never filtered — sometimes the earlier rollback was the mistake. */
  knownBad: boolean;
  rolledBackByRunId: string | null;
  isDefault: boolean;
}
export interface RollbackTargetsResponse {
  targets: RollbackTarget[];
  currentSha: string;
}

export interface PriorShaResponse {
  runId?: string | null;
  sha: string | null;
  shortSha: string | null;
  succeededAt: string | null;
  initiator: string | null;
}

/**
 * Response shape for `/approvals/:reqId/eligibility` (Scope C.0).
 * Drives the eligibility banner on the approval review surface.
 */
export interface EligibilityResponse {
  canApprove: boolean;
  isInitiator: boolean;
  isAlreadyDecided: boolean;
  /**
   * Decision 17 — viewer is the initiator AND holds deploy.emergency-approve:
   * the review surface offers break-glass self-approve (reason required).
   * Optional: older backends don't send it.
   */
  canEmergencySelfApprove?: boolean;
  decidedBy: {
    login: string;
    at: string;
    action: 'approved' | 'rejected';
  } | null;
}

/** Per-approver entry in `/approvals/:reqId/approvers` (Scope C.1). */
export interface ApproverEntry {
  login: string;
  handle: string;
  viewedAt: string | null;
}

export interface ApproversResponse {
  approvers: ApproverEntry[];
}

/** Commit entry returned from `/approvals/:reqId/commits` (Scope C.2). */
export interface CommitEntry {
  sha: string;
  shortSha: string;
  message: string;
  author: string;
  authoredAt: string;
}

export interface CommitsResponse {
  /**
   * PLAT-468 — the schema delta of the deploy under review. `unknown` means
   * the compare could not be read; render that as unknown, never as none.
   * Absent on older backends.
   */
  migrations?: BranchDiffMigrations;
  commits: CommitEntry[];
  base: string | null;
  head: string;
  /**
   * Real infra project id for the service (resolved server-side via the
   * Bug 29 reverse map). Optional: older backends don't send it — the
   * review page then omits the manifest-target path rather than guessing.
   */
  projectId?: string;
  /**
   * Per-service deployed bases (2026-07-23) — a multi-service approval
   * diffs each deployed service; `base` is null when bases diverge and
   * these rows carry the truth. Optional: older backends don't send it.
   */
  perService?: Array<{ service: string; base: string | null }>;
  /**
   * The initiator's required prod justification (2026-07-30). Collected at
   * submit, stored on the run, and previously never shown to the approver.
   * Optional: older backends don't send it.
   */
  reason?: string | null;
  /** Deployed image groups + workload/project totals — the blast radius. */
  scope?: {
    services: string[];
    workloadCount: number | null;
    projectServiceCount: number | null;
  };
  /** Head commit subject + author, resolved independently of the diff. */
  headCommit?: {
    subject: string | null;
    authorLogin: string | null;
  } | null;
}

// Response shape for /services-for-build (Scope B.0, refactored in F2.3
// per PRD-2026-06-03-services-info-canonicalization).
//
// Image groups are now bucketed by SEMANTIC name (backend, frontend,
// events-hook, ...) joined from services-info.yaml — not by workflow
// file. The workflow file is exposed as a secondary `workflowFile`
// field so the modal can render it as a small badge per the v2-
// prototype .image-group-badge pattern.
//
// `name` is the canonical group identifier; matches both image-
// groups.yaml's groupKey AND services-info.yaml's `image:` field.
//
// `skippedImageGroups` lists groups that were declared in image-
// groups.yaml but had no matching `image:` in this env's services-
// info.yaml. Frontend can optionally surface this as a debug hint;
// Phase 1 doesn't render it.
export interface ServicesForBuildImageGroup {
  name: string;
  workflowFile: string;
  services: string[];
  serviceCount: number;
}

export interface ServicesForBuildResponse {
  branch: string;
  headSha: string | null;
  imageGroups: ServicesForBuildImageGroup[];
  env: string;
  skippedImageGroups: string[];
  unavailableAtSource?: string[];
}

// FE-8 — the set of services DEPLOYABLE in a given (project, env): those
// with a kustomize overlay in projects/<project>/manifest/overlays/<env>/.
// The deploy-branch modal intersects this with each group's services so it
// only offers workloads that have a manifest to patch. `services` is null
// when the backend couldn't determine the set (fail-open → treat all as
// deployable, the pre-FE-8 behavior).
export interface DeployableServicesResponse {
  project: string;
  env: string;
  services: string[] | null;
}

// Response shape for POST /cache/services-info — manual refresh from
// the deploy modal's refresh button. `refetched` is the freshly-pulled
// payload (null on failure; modal falls back to calling /services-for-
// build itself, which will see an empty cache and re-pull).
export interface ServicesInfoCacheBustResponse {
  evicted: boolean;
  env: string;
  ref: string;
  owner: string;
  repo: string;
  refetched: unknown | null;
}

/** Per-dispatch entry in the POST /build response (PR #128). */
export interface BuildDispatchResult {
  workflow: string;
  imageGroups: string[];
  /** GitHub workflow run id; null when discovery is best-effort. */
  runId: number | null;
  /** The run's head_sha when discovered, else null. Optional — older
   *  backends omit it. */
  headSha?: string | null;
}

/** Request shape for POST /build (PR #128). */
export interface BuildRequest {
  buildRepo: string;
  branch: string;
  imageGroups: string[];
  /**
   * Display scope for the "building…" row in Recent builds — the project
   * whose Build-new page fired this. Optional for backward compat; without
   * it the dispatch isn't recorded and no pending row shows.
   */
  projectId?: string;
}

/** Response shape for POST /build (PR #128). */
export interface BuildResponse {
  dispatched: BuildDispatchResult[];
}

/** Per-service aggregate row in `/diff-summary` (Scope B.1). */
export interface DiffSummaryPerService {
  service: string;
  /**
   * PLAT-468 — the schema delta for this service. `unknown` means the delta
   * could not be read; it is never "no migrations". Absent on older backends.
   */
  migrations?: BranchDiffMigrations;
  commitsAhead: number;
  /** Reverse GitHub compare count when the deployed source is ahead. */
  commitsBehind?: number;
  /** Explicit source identity. Absent on older servers, so zero ahead is unknown. */
  sameSha?: boolean;
  totalCommits: number;
  /**
   * True when the overlay → current-SHA resolution succeeded and the
   * commit comparison ran. False when we couldn't determine the
   * current SHA (e.g., AR digest unmapped). When false, commitsAhead=0
   * does NOT mean "no diff" — it means "unknown".
   *
   * Defaults to `false` if missing for backward-compat with an
   * older backend that didn't include the field.
   */
  available?: boolean;
}

export interface DiffSummaryResponse {
  aggregate: {
    commitsAhead: number;
    contributors: number;
    prs: number;
    mostRecent: { sha: string; message: string; author: string } | null;
    /** See DiffSummaryPerService.available — aggregate is true iff at least one per-service succeeded. */
    available?: boolean;
  };
  perService: DiffSummaryPerService[];
  /**
   * Deduped ahead-commits across services, newest-first, capped at 30.
   * Optional — older backends omit it; the modal hides the drawer then.
   */
  commits?: BranchCommit[];
}

/** Per-service row in `/branch-diff` (Scope B.2). */
/** One commit the branch is ahead of main (R-4.3 expandable list). */
export interface BranchCommit {
  sha: string;
  message: string;
  author: string;
  authoredAt: string | null;
}

/** Per image-group, path-aware diff vs main (PRD §9.2). */
export interface BranchDiffPerGroup {
  groupKey: string;
  hasDiff: boolean;
  /** Changed files mapped to this group (0 when coarse/unknown). */
  changedFileCount: number;
  /** true = real paths-filter match; false = coarse fallback (branch ahead). */
  pathAware: boolean;
}

/**
 * PLAT-468 — the schema half of the delta. `verdict: 'unknown'` means the
 * changed-file list could not be read in full; render it as unknown, never as
 * none.
 */
export interface BranchDiffMigrations {
  verdict: 'none' | 'present' | 'unknown';
  /** A floor, not a total, when `complete` is false. */
  count: number;
  apps: string[];
  files: Array<{
    app: string;
    number: string;
    name: string;
    path: string;
    status: string;
    /**
     * Django operations declared in the migration, in order. Null = the body
     * could not be read (truncated or unparseable patch) — render that as
     * "operations unreadable", never as "no operations".
     */
    operations?: string[] | null;
  }>;
  complete: boolean;
  /**
   * Migrations the deploy goes back past: in the code currently deployed,
   * absent from the code being shipped. Set only when the target is behind
   * the deployed commit (a rollback) or has diverged from it; absent
   * otherwise. The fields above keep describing what the deploy CARRIES.
   *
   * A deploy never reverses a migration (every environment runs a plain
   * `migrate`, which skips migrations the code lacks), so these stay applied
   * and the older code runs against the schema they left. `unknown` means the
   * deployed side could not be read in full. Its own `back` is never set.
   * Absent on older backends.
   */
  back?: BranchDiffMigrations | null;
}

export interface BranchDiffResponse {
  branchHeadSha: string | null;
  mainHeadSha: string | null;
  /** Commits ahead of the diff base (repo-level). */
  ahead: number;
  /** Commits behind the diff base (repo-level) — downgrade signal (R-4.3). */
  behind: number;
  /** Ahead commits, newest-first, capped. */
  commits: BranchCommit[];
  perGroup: BranchDiffPerGroup[];
  /** true iff at least one group resolved via its workflow paths-filter. */
  pathAware: boolean;
  /** Absent on older backends that never computed it — render nothing then. */
  migrations?: BranchDiffMigrations;
  /**
   * FE-9 — the SHA the diff was computed against (main HEAD for the legacy
   * "vs main" path, or the target env's currently-deployed SHA for an
   * env-relative diff). May be null on the no-octokit/no-heads fallbacks.
   */
  baseSha?: string | null;
  /**
   * FE-9 — which baseline the ahead/behind reflect: 'main' (vs main HEAD) or
   * 'env' (vs what's currently deployed in the target env). Absent on older
   * backends → treat as 'main'.
   */
  baseKind?: 'main' | 'env';
  /**
   * FE-9 — readiness signal: true = the image already exists in AR (the deploy
   * will skip the build → fast), false = a build will run, null/absent = not
   * resolvable (render no readiness line). Advisory only; never gates deploy.
   */
  imageReady?: boolean | null;
  /** Present only when the selected workloads were checked together. */
  imageReadiness?: Array<{
    imageGroup: string;
    ready: boolean;
    sourceSha: string | null;
  }>;
}

/** Response shape for `/project-header-state` (Scope D.1). */
export interface ProjectHeaderStateResponse {
  lastSuccess: {
    sha: string;
    shortSha: string;
    succeededAt: string;
    initiator: string;
  } | null;
  healthSummary: {
    totalRecent: number;
    succeeded: number;
    failed: number;
    cancelled: number;
    allHealthy: boolean;
  } | null;
}

export type PermissionDecision = 'ALLOW' | 'DENY' | 'CONDITIONAL';

export interface PermissionsSelf {
  // The backend returns AuthorizeResult values from
  // @backstage/plugin-permission-common, which are strings.
  view: PermissionDecision;
  trigger: PermissionDecision;
  approve: PermissionDecision;
}

/**
 * Per-branch commit metadata returned by `/branches` for the top-20
 * most-recently-committed branches (PR #128). Older branches return
 * just `{ name, sha, protected }` and the picker renders them as
 * compact rows in the "All branches" section.
 */
export interface BranchCommitMeta {
  shortMessage: string;
  author: string;
  /** ISO 8601. Falls back to epoch when unknown. */
  authoredAt: string;
}

export interface BranchInfo {
  name: string;
  sha: string;
  protected: boolean;
  commit?: BranchCommitMeta;
}

export interface BranchPickerResponse {
  entityRef: string;
  repo: string;
  branches: BranchInfo[];
  warning?: 'github_not_configured';
}

export interface MainCommitInfo {
  sha: string;
  subject: string;
  author: string | null;
  committedAt: string | null;
}

export interface MainCommitsResponse {
  entityRef: string;
  repo: string;
  commits: MainCommitInfo[];
  nextPage: number | null;
  /** GitHub's ordinary commit listing only permits bounded message search. */
  searchLimited: boolean;
}

/**
 * PLAT-468 — `/branch-migrations`: the migrations each branch head adds
 * compared with the default branch, for the branch picker's tags. Keyed by the
 * requested head SHA. Forward side only: a summary here never carries `back`.
 * A compare that could not be read in full answers `verdict: 'unknown'`,
 * never `none`. A null result (every result, with
 * `warning: 'github_not_configured'`) is no answer, not "no migrations".
 */
export interface BranchMigrationsResponse {
  entityRef: string;
  /** "owner/name". */
  repo: string;
  /** Default branch name the heads were compared with, e.g. "main". */
  base: string;
  results: Record<string, MigrationSummary | null>;
  warning?: 'github_not_configured';
}

export interface CreateDeployInput {
  entityRef: string;
  env: Env;
  sha: string;
  branch: string;
  /** Server-stored, actor-bound manifest preview reviewed for this selection. */
  manifestPreviewId?: string;
  reason?: string;
  /**
   * Bug 23 — multi-service deploy. Array of service identifiers (kebab-case
   * keys from the build repo's image-groups.yaml) the user selected in the
   * DeployBuildModal. The backend stores the list verbatim; the CI poller
   * comma-joins it as the manifest workflow's `service_name` input.
   *
   * Optional / omitted / empty array → single-service fallback: the
   * manifest workflow's service_name input is derived from the row's
   * imageGroup (set by recordBuildDispatched during the build phase).
   *
   * Matches v2-prototype.html#screen-deploy-modal line 4245:
   *   service_name=backend,backend-cloudflare-logpush-cron,...,frontend
   */
  imageGroups?: string[];
  /** Approved Dev2 backend activity test route; omitted for normal runs. */
  testSlackChannelId?: string;
  /**
   * Scope A/D3 — the run this deploy reverts away from. Makes a rollback a
   * normal deploy that happens to carry lineage, instead of a second
   * mechanism. Forgeable by construction, so the server grants nothing on it
   * without proof; see deploy.ts `genuineRollback`.
   */
  rollbackFromRunId?: string;
  /** Exact successful run whose immutable image plan will be replayed. */
  rollbackTargetRunId?: string;
}

export interface ManifestPreviewResponse {
  id: string;
  projectId: string;
  env: Env;
  revision: string;
  rows: Array<{
    service: string;
    imageGroup: string;
    currentDigest: string | null;
    targetDigest: string | null;
  }>;
}

export interface CreateDeployResponse {
  id: string;
  status: RunStatus;
  approvalRequestId: string | null;
  approvers?: string[]; // populated for env=prod (PENDING_APPROVAL)
  quorum?: number;
  expiresAt?: string;
  warning?: 'github_not_configured';
}

// ── Production-readiness types (mirror the backend handler shapes) ─

export interface ProjectServiceMeta {
  name: string;
  /** Workflow filename from image-groups.yaml (e.g. "ci.yml"). Null when
   *  the build repo doesn't have image-groups.yaml. */
  workflow: string | null;
}

export interface ProjectMetadata {
  projectId: string;
  envs: string[];
  /**
   * The project's approval-gated envs: prod when configured, plus its
   * `deployV3.approvalEnvs` entries (PLAT-668). Pass to
   * `requiresApproval(env, approvalEnvs)`. Absent from older backends,
   * which gate prod only.
   */
  approvalEnvs?: string[];
  services: ProjectServiceMeta[];
}

/**
 * Cell-status enum (matches backend `CellStatus` in
 * `handlers/deploymentState.ts`):
 *   - in_sync             — currentSha == main HEAD
 *   - behind              — currentSha is behind main
 *   - unknown             — digest known, no diff computed yet
 *   - unavailable         — overlay exists but digest extraction failed
 *   - absent_from_overlay — overlay file doesn't exist for this (project,
 *                           env, service) — service configured but never
 *                           shipped here (Scope D.0)
 */
export type CellStatus =
  | 'in_sync'
  | 'behind'
  | 'unknown'
  | 'unavailable'
  | 'absent_from_overlay';

/**
 * Commit metadata for the SHA currently deployed in a cell. Surfaces as
 * "2h ago · @aniket" under the SHA pill (prototype-parity task §Scope 1).
 */
export interface CellCommitMeta {
  authorLogin: string | null;
  authorName: string | null;
  authoredAt: string | null;
  shortMessage: string | null;
  /**
   * FE-3 parity (G2) — the FULL commit message, capped server-side at 600
   * chars (with a trailing '…' when clipped). Always present on /builds
   * rows; the deployment-state endpoints don't include it (optional here
   * because this shape is shared with DeploymentStateCell.commit).
   */
  fullMessage?: string | null;
}

export interface DeploymentStateCell {
  serviceName: string;
  imageGroup: string | null;
  currentSha: string | null;
  currentDigest: string | null;
  deployedAt: string | null;
  deployedBy: string | null;
  /**
   * Login of the latest successful run's requester only when that run's
   * recorded image digest matches this cell. Null when attribution cannot
   * be verified; distinct from commit.authorLogin.
   */
  deployedByLogin: string | null;
  /** Successful run matched to the observed image digest; absent without proof. */
  lastVerifiedRunId?: string;
  /** Source branch captured on a run whose image digest matches this cell. */
  deployedBranch?: string | null;
  status: CellStatus;
  sourceRepo: string | null;
  commit: CellCommitMeta | null;
}

export interface DeploymentStateRow {
  projectId: string;
  env: string;
  services: DeploymentStateCell[];
}

/**
 * Response shape of the single-cell `GET /deployment-state?...&service=X`
 * endpoint — a SINGLE `service` cell (NOT a `services` array; that's the
 * matrix/row shape). FE-9 reads `service.currentSha` (a full 40-hex git SHA)
 * as the env-relative diff base.
 */
export interface DeploymentStateSingle {
  projectId: string;
  env: string;
  service: DeploymentStateCell;
  generatedAt?: string;
}

/**
 * Per-service drift summary, surfaced when two envs of the same service
 * disagree on currentSha. Used by both the StatusCell border styling and
 * the project-level DriftBanner.
 */
export interface ServiceDriftSummary {
  serviceName: string;
  aheadEnv: string;
  behindEnv: string;
  behindBy: number;
  /** 7-char shortened SHA from the ahead env's cell (Scope D.5). */
  aheadSha: string;
  /** 7-char shortened SHA from the behind env's cell (Scope D.5). */
  behindSha: string;
}

/** FE-5 — per-workload drill-in rows for one (project, group). */
export interface WorkloadCellState {
  digest: string | null;
  sha: string | null;
  overlayExists: boolean;
}
export interface DeploymentStateWorkloads {
  projectId: string;
  group: string;
  envs: string[];
  rows: Array<{
    workload: string;
    cells: Record<string, WorkloadCellState>;
  }>;
}

export interface DeploymentStateMatrix {
  generatedAt: string;
  cacheAge: 'fresh' | string;
  rows: DeploymentStateRow[];
  drift: Record<string, ServiceDriftSummary[]>;
}

export interface DiffResponse {
  currentSha: string | null;
  mainSha: string | null;
  behindBy: number;
  totalCommits: number;
  sameAsMain: boolean;
  sourceRepo: string | null;
}

// ── Builds (FE-3 Recent-builds table) ──────────────────────────────
//
// The GET /builds response aggregates by SHA: one commit/build → one
// BuildRow, listing EVERY image-group built at that SHA, each with its OWN
// digest + registry. This replaces the pre-FE-3 per-image BuildItem rows
// (which surfaced the same SHA N times, once per service). The columnar
// "Recent builds" table (RecentBuildsRail) renders one row per BuildRow:
// SHA · Services · Branch · CI · Author · action, expanding to a per-service
// ARTIFACTS list.

/** One image-group artifact built at a BuildRow's SHA (>= 1 per row). */
export interface BuildArtifact {
  /** Image-group primary serviceName (e.g. "backend", "frontend"). */
  service: string;
  /** AR image name. */
  imageName: string;
  /** Bare 64-hex digest (no sha256: prefix) — same as the old artifactDigest. */
  digest: string;
  /** Fully-qualified AR path "<loc>-docker.pkg.dev/.../<image>@sha256:<digest>". */
  registryUrl: string;
  /** Build workflow filename (e.g. "ci.yml"), or null. */
  workflow: string | null;
  /**
   * FE-3 parity (G6) — GitHub Actions workflow run id from
   * built_images.build_run_id for (imageGroup, gitSha). Null when the build
   * wasn't dispatched through v3 (no run on record). Drives the direct
   * "CI run ↗" link `https://github.com/<sourceRepo>/actions/runs/<id>`.
   */
  buildRunId: number | null;
  /**
   * When THIS artifact was built (ISO), or null. Per-artifact because the
   * same SHA can gain artifacts hours apart (merge CI vs manual dispatch).
   */
  builtAt?: string | null;
  /**
   * The Backstage user whose dispatch produced this artifact, when known.
   * Optional — older backends omit it. The row's AUTHOR column is the
   * COMMIT author; this is the BUILD initiator.
   */
  builtBy?: string | null;
}

/** One commit/build aggregated by SHA — the Recent-builds table row. */
export interface BuildRow {
  sha: string | null;
  shortSha: string | null;
  /**
   * Newest builtAt among the row's artifacts (ISO). For CI-failed rows
   * this is the failure time (keeps the newest-first sort honest).
   */
  builtAt: string | null;
  /** Parsed from the AR deploy tag prefix. */
  branch: string | null;
  /**
   * AR-surfaced builds = 'passed' (the image published); 'failed' rows are
   * unioned in from FAILED build-run children (nothing published);
   * 'building' = dispatch whose linked run hasn't concluded;
   * 'registering' = run concluded SUCCESS but artifacts haven't registered
   * on the rail yet (AR sync lag) — CI truth, never a stale "building…".
   */
  ciStatus: 'passed' | 'failed' | 'unknown' | 'building' | 'registering';
  /** PR number parsed from the FULL commit message, or null ("—"). */
  prNumber: number | null;
  /**
   * FE-3 parity (P1) — "owner/repo" the commit lives in (from the build
   * entry's sourceRepo). Drives DIRECT GitHub links (commit / pull /
   * actions runs); null → fall back to github.com/search URLs.
   */
  sourceRepo: string | null;
  commit: CellCommitMeta | null;
  /**
   * Collapsed service tags: artifacts.map(a => a.service) for AR rows; the
   * failed image-group keys for CI-failed rows.
   */
  services: string[];
  /** >= 1, deduped by imageName — except CI-failed rows, which have []. */
  artifacts: BuildArtifact[];
  /**
   * Direct GitHub Actions run URL for CI-failed rows ("View logs ↗").
   * Null/absent for AR rows. Optional for back-compat with older backends.
   */
  ciRunUrl?: string | null;
}

/**
 * Back-compat alias. Pre-FE-3 callers imported `BuildItem`; the type is now
 * the SHA-aggregated BuildRow. Kept so existing imports don't break.
 */
export type BuildItem = BuildRow;

export interface BuildsResponse {
  generatedAt: string;
  /** F5 — window scan time; drives the rail's "as of HH:MM" stamp. Optional: older backends omit it. */
  fetchedAt?: string;
  builds: BuildRow[];
  /** rb.22 pager metadata — server-side pagination. */
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  /**
   * Distinct services, author logins, and branches in the recent scan window
   * (NOT the filtered page) — populate the filter dropdowns from these so
   * they stay complete after a filter narrows the rows. Server-derived
   * (Rule 4).
   */
  availableServices: string[];
  availableAuthors: string[];
  availableBranches: string[];
}

// ── Error type ─────────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    public readonly httpStatus: number,
    public readonly code: string, // 'denied' | 'locked_by_other_run' | 'two_person_rule' | ...
    public readonly body: Record<string, unknown> = {},
  ) {
    super(`${code} (${httpStatus})`);
    this.name = 'ApiError';
  }
}

// ── Interface ──────────────────────────────────────────────────────
export interface EnvEndpoint {
  service: string;
  host: string;
}
/** Env allocation from env_slots.yaml: its own cluster, or a namespace
 *  inside the named cluster-mode env. */
export interface EnvSlot {
  mode: 'cluster' | 'namespace';
  cluster?: string;
}
export interface EnvEndpointsResponse {
  projectId: string;
  endpoints: Record<string, EnvEndpoint[] | null>;
  /** null — env_slots.yaml unreadable (fail-open): omit namespace lines. */
  slots: Record<string, EnvSlot> | null;
}

export interface DeployV3Api {
  permissionsSelf(): Promise<PermissionsSelf>;
  headerState(entityRef: string): Promise<HeaderState>;
  /**
   * `env` accepts a single Env or a CSV string ("dev,qa,prod"). Multi-env
   * form returns envCounts in the response (Scope D.4).
   */
  listRuns(filter: {
    entityRef?: string;
    env: Env | string;
    limit?: number;
    cursor?: string;
  }): Promise<ListRunsResponse>;
  /** Prior-SHA lookup for rollback affordance (Scope D.2). */
  getPriorSha(
    opts:
      | { projectId: string; env: Env; service: string; excludeSha?: string }
      /** Scope A — resolve the lane from a RUN, so a caller holding a run
       *  never has to infer a service name (#362/#399/#428). */
      | { runId: string },
  ): Promise<PriorShaResponse>;
  /**
   * D3 — SHAs rolled back out of this project's lanes in `env`. Drives the
   * "↩ rolled back" marker on the builds rail and the deploy-confirm warning.
   * Warns, never blocks: sometimes the rollback was itself the mistake.
   */
  getKnownBadShas(opts: {
    projectId: string;
    env: Env;
  }): Promise<KnownBadResponse>;
  /**
   * D2 — candidates for a rollback, newest first, each flagged if it was
   * itself fled before. "Last good" is a default, not the only option: in a
   * cascade the previous SHA is bad too, and that is when rollback matters
   * most.
   */
  getRollbackTargets(opts: { runId: string }): Promise<RollbackTargetsResponse>;
  /** Project-level header-state (Scope D.1) — drives the calm banner on home-prod. */
  getProjectHeaderState(opts: {
    projectId: string;
    env: Env;
  }): Promise<ProjectHeaderStateResponse>;
  /** Approval-eligibility lookup (Scope C.0) — drives the
   *  green/red/info banners on the approval review surface. */
  getApprovalEligibility(reqId: string): Promise<EligibilityResponse>;
  /** Approvers list with view-status (Scope C.1) — drives the A1
   *  approvers block. Also records the caller's view server-side. */
  getApprovalApprovers(reqId: string): Promise<ApproversResponse>;
  /** Commits between current prod SHA and request SHA (Scope C.2). */
  getApprovalCommits(reqId: string): Promise<CommitsResponse>;
  // Image groups for the deploy-branch / build-new pickers (Scope B.0,
  // refactored in F2.3). Bucketed by semantic image-group name from
  // services-info.yaml (totetech/infra) joined with image-groups.yaml
  // (build repo). `env` is required so the backend can read the
  // right services-info.yaml.
  getServicesForBuild(opts: {
    buildRepo: string;
    env: string;
    branch?: string;
    sourceSha?: string;
  }): Promise<ServicesForBuildResponse>;
  // FE-8 — deployable service set for the selected deploy-env. The modal
  // disables workloads not deployable in that env. Resolves to
  // `{ services: null }` on any error (fail-open).
  getDeployableServices(opts: {
    project: string;
    env: Env;
  }): Promise<DeployableServicesResponse>;
  // F2.3 — manual refresh button. Invalidates the backend's 24h cache
  // of services-info.yaml for this env and (on success) returns the
  // freshly-pulled payload. Modal calls this when the user clicks the
  // refresh affordance.
  invalidateServicesInfoCache(opts: {
    env: string;
  }): Promise<ServicesInfoCacheBustResponse>;
  // Fire CI workflow_dispatch for picked image groups (PR #128).
  dispatchBuild(input: BuildRequest): Promise<BuildResponse>;
  /** Multi-service diff aggregate (Scope B.1). */
  getDiffSummary(opts: {
    projectId: string;
    env: Env;
    services: string[];
    sha: string;
  }): Promise<DiffSummaryResponse>;
  /** Group-first, path-aware branch-diff pre-check (Scope B.2; PRD §9.2 + R-4.3).
   *  FE-9: pass `baseSha` (the target env's currently-deployed SHA) for an
   *  env-relative diff. Readiness requires projectId, env, selected workload
   *  services, readinessSha and a readyGroup principal. Legacy callers may omit readiness. */
  getBranchDiff(opts: {
    buildRepo: string;
    branch: string;
    groups: string[];
    baseSha?: string | null;
    readyGroup?: string | null;
    env?: Env;
    projectId?: string;
    services?: string[];
    /** Selection-only readiness at a loaded SHA; skips branch/diff computation. */
    readinessSha?: string;
  }): Promise<BranchDiffResponse>;
  getRun(runId: string): Promise<DeployRun>;
  previewManifest(input: Pick<CreateDeployInput, 'entityRef' | 'env' | 'sha' | 'branch' | 'imageGroups'>): Promise<ManifestPreviewResponse>;
  createDeploy(input: CreateDeployInput): Promise<CreateDeployResponse>;
  /**
   * Cancel an in-flight run. `reason` (10-500 chars) is required by the
   * in-drawer confirm — it lands in the audit log and the run's Slack
   * thread. Uses the POST route; the legacy DELETE (reasonless) remains
   * server-side for old clients.
   */
  cancelRun(runId: string, reason: string): Promise<DeployRun>;
  /**
   * Re-run a FAILED / CANCELLED run: the backend creates a NEW run replaying
   * the source run's exact parameters (sha, branch, env, services). Response
   * is the fresh-deploy response (id + status; approval fields on prod).
   */
  rerun(runId: string): Promise<CreateDeployResponse>;
  /**
   * Scope 3 (partial-build-recovery): deploy the BUILT subset of a FAILED
   * split-outcome run. Backend creates a NEW deploy-only run (images exist →
   * fastpath skips CI) carrying parentRunId + the skipped-groups snapshot.
   * 409 when the run isn't FAILED or the outcome isn't split.
   */
  deployBuilt(runId: string): Promise<CreateDeployResponse>;
  /** Omit `priorSha` to let the backend resolve the most recent SUCCEEDED
   *  run in this lane ("last good"); pass one to target a specific SHA. */
  rollback(runId: string, priorSha?: string): Promise<DeployRun>;
  takeOver(
    runId: string,
    body: { sha: string; branch: string; reason: string },
  ): Promise<{ newRun: DeployRun; oldRun: DeployRun }>;
  listBranches(
    entityRef: string,
    q?: string,
    opts?: { refresh?: boolean },
  ): Promise<BranchPickerResponse>;
  listMainCommits(entityRef: string, page?: number, q?: string): Promise<MainCommitsResponse>;
  /**
   * PLAT-468 — migrations each branch head adds compared with the default
   * branch (the picker's row tags). `heads`: 1-20 full 40-hex SHAs. Does not
   * depend on an environment.
   */
  getBranchMigrations(opts: {
    entityRef: string;
    heads: string[];
  }): Promise<BranchMigrationsResponse>;

  // ── Production-readiness endpoints ───────────────────────────────
  /** Per-project metadata: envs + services. Drives env-chip filtering
   *  in the deploy form and the column list in StatusGrid. */
  listProjects(): Promise<ProjectMetadata[]>;
  /** Per-project deployment-state matrix. Scoped to one project to keep
   *  the backend's per-request fan-out bounded — calling this without a
   *  projectId is supported but discouraged (returns ALL projects, which
   *  can hit hundreds of GitHub API calls per request and trip the
   *  Envoy rate limiter). */
  /** Env-cards PRD — per-env public endpoints parsed from the env's
   *  HTTPRoute overlay files. `null` for an env = transient read
   *  failure (fail-open): render the card without an endpoints section. */
  envEndpoints(projectId: string): Promise<EnvEndpointsResponse>;
  /** FE-5 — lazy per-group workload drill-in (fetched on grid expand). */
  deploymentStateWorkloads(
    projectId: string,
    group: string,
  ): Promise<DeploymentStateWorkloads>;
  deploymentStateMatrix(projectId?: string): Promise<DeploymentStateMatrix>;
  /** Single-cell variant used when the matrix isn't loaded yet. */
  deploymentState(opts: {
    projectId: string;
    env: string;
    service?: string;
  }): Promise<DeploymentStateSingle>;
  /** Per-(service, env) diff between deployed SHA and main HEAD.
   *  Drives the confirm-modal diff badges and smart no-op. */
  getDiff(opts: {
    service: string;
    env: Env;
    projectId?: string;
  }): Promise<DiffResponse>;
  /** Recent buildable SHAs for a project from Artifact Registry. Drives
   *  the "Recent builds" rail on the home view. */
  listBuilds(opts: {
    projectId: string;
    /** rb.22 page size → ?builds_limit. */
    limit?: number;
    /** rb.22 1-based page → ?builds_page. */
    page?: number;
    /** Server-side filters + sort (Rule 4). */
    service?: string;          // → ?build_service
    author?: string;           // → ?build_author
    branch?: string;           // → ?build_branch
    status?: 'passed' | 'failed' | 'building';  // → ?build_status
    /** Row sort order (default 'newest'). → ?builds_sort */
    sort?: 'newest' | 'oldest';
  }): Promise<BuildsResponse>;

  // Approval endpoints — used by the (Sprint 5+) approval inbox screens.
  // Without `env` the backend lists pending requests in every gated env
  // (prod, plus each project's deployV3.approvalEnvs — PLAT-668).
  listApprovals(filter: {
    status: 'pending';
    env?: Env;
  }): Promise<{ items: ApprovalRequest[] }>;
  getApproval(reqId: string): Promise<ApprovalRequest>;
  approve(reqId: string, opts?: { reason?: string }): Promise<ApprovalRequest>;
  reject(reqId: string, reason: string): Promise<ApprovalRequest>;
  cancelApproval(reqId: string): Promise<ApprovalRequest>;
}

// ── Factory + ApiRef ───────────────────────────────────────────────
export interface DeployV3ApiOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

export const deployV3ApiRef = createApiRef<DeployV3Api>({
  id: 'plugin.deploy-v3.service',
});

export function createDeployV3Api({
  discoveryApi,
  fetchApi,
}: DeployV3ApiOptions): DeployV3Api {

  async function baseUrl(): Promise<string> {
    return discoveryApi.getBaseUrl('deploy-v3');
  }

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await baseUrl()}${path}`;
    let resp: Response;
    try {
      resp = await fetchApi.fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...init,
      });
    } catch (e) {
      throw new ApiError(0, 'network', {
        message: (e as Error).message ?? 'fetch failed',
      });
    }
    const text = await resp.text();
    let body: Record<string, unknown> = {};
    if (text) {
      try {
        body = JSON.parse(text) as Record<string, unknown>;
      } catch {
        // Non-JSON body — wrap into a generic error
        body = { raw: text };
      }
    }
    if (!resp.ok) {
      const code =
        typeof body.error === 'string'
          ? (body.error as string)
          : `http_${resp.status}`;
      throw new ApiError(resp.status, code, body);
    }
    return body as T;
  }

  return {
    permissionsSelf: () => request('/permissions/self'),

    headerState: (entityRef: string) =>
      request(`/header-state?entityRef=${encodeURIComponent(entityRef)}`),

    getPriorSha: opts => {
      if ('runId' in opts) {
        const q = new URLSearchParams({ runId: opts.runId });
        return request<PriorShaResponse>(`/prior-sha?${q.toString()}`);
      }
      const q = new URLSearchParams({
        projectId: opts.projectId,
        env: opts.env,
        service: opts.service,
      });
      if (opts.excludeSha) q.set('excludeSha', opts.excludeSha);
      return request<PriorShaResponse>(`/prior-sha?${q.toString()}`);
    },

    getRollbackTargets: ({ runId }) =>
      request<RollbackTargetsResponse>(
        `/rollback-targets?runId=${encodeURIComponent(runId)}`,
      ),

    getKnownBadShas: ({ projectId, env }) => {
      const q = new URLSearchParams({ projectId, env });
      return request<KnownBadResponse>(`/known-bad?${q.toString()}`);
    },

    getProjectHeaderState: ({ projectId, env }) => {
      const q = new URLSearchParams({ projectId, env });
      return request<ProjectHeaderStateResponse>(
        `/project-header-state?${q.toString()}`,
      );
    },

    getApprovalEligibility: (reqId: string) =>
      request<EligibilityResponse>(
        `/approvals/${encodeURIComponent(reqId)}/eligibility`,
      ),

    getApprovalApprovers: (reqId: string) =>
      request<ApproversResponse>(
        `/approvals/${encodeURIComponent(reqId)}/approvers`,
      ),

    getApprovalCommits: (reqId: string) =>
      request<CommitsResponse>(
        `/approvals/${encodeURIComponent(reqId)}/commits`,
      ),

    getServicesForBuild: ({ buildRepo, env, branch, sourceSha }) => {
      // F2.3 — env is now required so the backend can read the right
      // services-info.yaml from infra. Falls through with a useful 400
      // if the caller forgot to pass it (rather than silently returning
      // an empty list).
      const q = new URLSearchParams({ buildRepo, env });
      if (branch) q.set('branch', branch);
      if (sourceSha) q.set('sourceSha', sourceSha);
      return request<ServicesForBuildResponse>(
        `/services-for-build?${q.toString()}`,
      );
    },

    getDeployableServices: async ({ project, env }) => {
      // FE-8 — fail-open: ANY error (route unavailable, network, 4xx/5xx)
      // resolves to `{ services: null }` so the modal treats every workload
      // as deployable (pre-FE-8 behavior). Never throws.
      const q = new URLSearchParams({ project, env });
      try {
        return await request<DeployableServicesResponse>(
          `/deployable-services?${q.toString()}`,
        );
      } catch (e) {
        // P1 (SILENT-FAILURE-AUDIT-2026-07-17): the FE-8 guard failing
        // OPEN with zero signal made a broken endpoint byte-identical to
        // "everything deployable". The fail-open stays (availability over
        // strictness) — but never silently.
        // eslint-disable-next-line no-console
        console.warn(
          `deploy-v3: /deployable-services failed for ${project}/${env} — FAILING OPEN (all workloads treated deployable)`,
          e,
        );
        return { project, env, services: null };
      }
    },

    invalidateServicesInfoCache: ({ env }) => {
      const q = new URLSearchParams({ env });
      return request<ServicesInfoCacheBustResponse>(
        `/cache/services-info?${q.toString()}`,
        { method: 'POST' },
      );
    },

    dispatchBuild: input =>
      request<BuildResponse>('/build', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    getDiffSummary: ({ projectId, env, services, sha }) => {
      const q = new URLSearchParams({
        projectId,
        env,
        services: services.join(','),
        sha,
      });
      return request<DiffSummaryResponse>(`/diff-summary?${q.toString()}`);
    },

    getBranchDiff: ({
      buildRepo, branch, groups, baseSha, readyGroup, projectId, env, services, readinessSha,
    }) => {
      const q = new URLSearchParams({
        buildRepo,
        branch,
        groups: groups.join(','),
      });
      if (baseSha) q.set('baseSha', baseSha);
      if (readyGroup) q.set('readyGroup', readyGroup);
      if (projectId) q.set('projectId', projectId);
      if (env) q.set('env', env);
      if (services) q.set('services', services.join(','));
      if (readinessSha) q.set('readinessSha', readinessSha);
      return request<BranchDiffResponse>(`/branch-diff?${q.toString()}`);
    },

    listRuns: ({ entityRef, env, limit, cursor }) => {
      const q = new URLSearchParams({ env });
      if (entityRef) q.set('entity', entityRef);
      if (limit !== undefined) q.set('limit', String(limit));
      if (cursor) q.set('cursor', cursor);
      return request(`/runs?${q.toString()}`);
    },

    getRun: (runId: string) => request(`/runs/${encodeURIComponent(runId)}`),

    previewManifest: input =>
      request('/manifest-preview', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    createDeploy: input =>
      request('/runs', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    cancelRun: (runId: string, reason: string) =>
      request(`/runs/${encodeURIComponent(runId)}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    rerun: (runId: string) =>
      request(`/runs/${encodeURIComponent(runId)}/rerun`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),

    deployBuilt: (runId: string) =>
      request(`/runs/${encodeURIComponent(runId)}/deploy-built`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),

    rollback: (runId: string, priorSha?: string) =>
      request(`/runs/${encodeURIComponent(runId)}/rollback`, {
        method: 'POST',
        body: JSON.stringify(priorSha ? { priorSha } : {}),
      }),

    takeOver: (runId: string, body) =>
      request(`/runs/${encodeURIComponent(runId)}/take-over`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    listBranches: (
      entityRef: string,
      q?: string,
      branchOpts?: { refresh?: boolean },
    ) => {
      const params = new URLSearchParams({ entity: entityRef });
      if (q && q.trim()) params.set('q', q.trim());
      if (branchOpts?.refresh) params.set('refresh', '1');
      return request(`/branches?${params.toString()}`);
    },

    listMainCommits: (entityRef: string, page = 1, q?: string) => {
      const params = new URLSearchParams({ entity: entityRef, page: String(page) });
      if (q?.trim()) params.set('q', q.trim());
      return request<MainCommitsResponse>(`/main-commits?${params.toString()}`);
    },

    getBranchMigrations: ({ entityRef, heads }) => {
      const q = new URLSearchParams({ entityRef, heads: heads.join(',') });
      return request<BranchMigrationsResponse>(
        `/branch-migrations?${q.toString()}`,
      );
    },

    listApprovals: ({ status, env }) =>
      request(`/approvals?status=${status}${env ? `&env=${env}` : ''}`),

    getApproval: (reqId: string) =>
      request(`/approvals/${encodeURIComponent(reqId)}`),

    approve: (reqId: string, approveOpts?: { reason?: string }) =>
      request(`/approvals/${encodeURIComponent(reqId)}/approve`, {
        method: 'POST',
        // Decision 17 — a break-glass self-approve carries its mandatory
        // reason; a normal approval sends an empty body as before.
        body: JSON.stringify(
          approveOpts?.reason ? { reason: approveOpts.reason } : {},
        ),
      }),

    reject: (reqId: string, reason: string) =>
      request(`/approvals/${encodeURIComponent(reqId)}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    cancelApproval: (reqId: string) =>
      request(`/approvals/${encodeURIComponent(reqId)}/cancel`, {
        method: 'POST',
        body: '{}',
      }),

    // ── Production-readiness endpoints ─────────────────────────────
    listProjects: () => request<ProjectMetadata[]>('/projects'),

    envEndpoints: (projectId: string) =>
      request<EnvEndpointsResponse>(
        `/env-endpoints?projectId=${encodeURIComponent(projectId)}`,
      ),

    deploymentStateWorkloads: (projectId: string, group: string) =>
      request(
        `/deployment-state-workloads?projectId=${encodeURIComponent(
          projectId,
        )}&group=${encodeURIComponent(group)}`,
      ),

    deploymentStateMatrix: (projectId?: string) => {
      const q = new URLSearchParams();
      if (projectId) q.set('projectId', projectId);
      const qs = q.toString();
      return request<DeploymentStateMatrix>(
        `/deployment-state-matrix${qs ? `?${qs}` : ''}`,
      );
    },

    deploymentState: ({ projectId, env, service }) => {
      const q = new URLSearchParams({ projectId, env });
      if (service) q.set('service', service);
      return request<DeploymentStateSingle>(
        `/deployment-state?${q.toString()}`,
      );
    },

    getDiff: ({ service, env, projectId }) => {
      const q = new URLSearchParams({ service, env });
      if (projectId) q.set('projectId', projectId);
      return request<DiffResponse>(`/diff?${q.toString()}`);
    },

    listBuilds: ({
      projectId,
      limit,
      page,
      service,
      author,
      branch,
      status,
      sort,
    }) => {
      const q = new URLSearchParams({ projectId });
      // rb.22 — prototype URL params (server-side pagination, Rule 4).
      if (limit !== undefined) q.set('builds_limit', String(limit));
      if (page !== undefined) q.set('builds_page', String(page));
      // Server-side filters + sort (Rule 4) — never client-side array.filter.
      // WIRE CONTRACT (audit-2 P0): the backend parses the BARE names
      // (req.query.service/author/branch/status/sort — builds.ts). The PAGE's
      // own URL uses build_*-prefixed params; those must not leak onto the
      // wire — the prefixed names silently no-op every filter server-side
      // (each side's unit tests still pass; only live integration catches it).
      if (service) q.set('service', service);
      if (author) q.set('author', author);
      if (branch) q.set('branch', branch);
      if (status) q.set('status', status);
      if (sort) q.set('sort', sort);
      return request<BuildsResponse>(`/builds?${q.toString()}`);
    },
  };
}
